import type { UseGetOptions } from "./useGet.type";
import type { ApiError, ApiPayloadGet } from "@/services/api";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import get from "lodash/get";

import { client } from "@/config/client";
import { QUERY_PERSIST_KEY } from "@/services/query";

export const useGet = <Data = unknown, Meta = unknown>(
  model: string,
  id?: string | number | null,
  options?: UseGetOptions<Data, Meta>
) => {
  const queryClient = useQueryClient();
  const cleanId = typeof id === "number" ? id : parseInt(id ?? "");

  const {
    params = {},
    rootId = null,
    endPoint = "get",
    neverStale = false,
    enabled = true,
    persist = false,
    queryKeyExtraParams = [],
    ...otherOptions
  } = options ?? {};

  const persistKey = persist ? [QUERY_PERSIST_KEY] : [];
  const queryKey = [
    model,
    endPoint,
    cleanId,
    rootId,
    params,
    ...queryKeyExtraParams,
    ...persistKey,
  ].filter(Boolean);

  const idIsInvalid = !id || isNaN(cleanId);

  const { data, ...queryResult } = useQuery<
    ApiPayloadGet<Data, Meta>,
    ApiError
  >({
    staleTime: neverStale ? Infinity : 0,
    ...otherOptions,
    enabled: idIsInvalid ? false : enabled,
    queryKey,
    queryFn: () => {
      const apiMethod = get(client, [model, endPoint]);

      return rootId
        ? apiMethod(rootId, cleanId, params)
        : apiMethod(cleanId, params);
    },
  });

  const removeQuery = () => {
    queryClient
      .cancelQueries({
        queryKey: queryKey,
      })
      .then(() => {
        queryClient.removeQueries({
          queryKey: queryKey,
        });
      });
  };

  return {
    ...queryResult,
    data: data?.data?.data,
    meta: data?.data?.meta,
    removeQuery,
  };
};
